import { formatLocalDate } from '../../utils/localDateTime';
import React from 'react';
import { ItineraryRiskResult, Page, NavParams } from '@types';
import { ICONS } from '../constants';
import UnifiedMap from '../UnifiedMap';
import useGoogleMaps from '../useGoogleMaps';
import { Section, md } from './ReportShared';
import RecommendedSuitesDisplay from './RecommendedSuitesDisplay';

interface ItineraryRiskDisplayProps {
    result: ItineraryRiskResult;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
}

const ItineraryRiskDisplay: React.FC<ItineraryRiskDisplayProps> = ({ result, onNavigate }) => {
    // Defensive guard: segments may be undefined if AI returned a non-conforming schema
    const segments: any[] = Array.isArray(result?.segments) ? result.segments : [];
    const { isLoaded } = useGoogleMaps();
    const routePoints = segments.flatMap((segment: any) => [segment.origin, segment.destination])
        .map((place: any) => ({ ...place, lat: Number(place?.lat), lng: Number(place?.lng), name: place?.name }))
        .filter((place: any) => Number.isFinite(place.lat)
            && Number.isFinite(place.lng)
            && (place.lat !== 0 || place.lng !== 0));
    const mapCenter = routePoints[0] || { lat: 20, lng: 0 };
    const tacticalRoutes = segments.map((segment: any, index: number) => {
        const origin = routePoints.find((point: any) => point.name === segment.origin?.name);
        const destination = routePoints.find((point: any) => point.name === segment.destination?.name);
        const usesRoads = /vehicle|driver|convoy|road|rail/i.test(String(segment.mode || ''));
        return {
            name: segment.legName || `Travel Leg ${index + 1}`,
            origin: usesRoads ? segment.origin?.name : '',
            destination: usesRoads ? segment.destination?.name : '',
            waypoints: [origin, destination].filter(Boolean) as any[],
            color: usesRoads ? '#2563eb' : '#f59e0b',
            isDash: !usesRoads
        };
    }).filter((route: any) => route.waypoints.length >= 2);
    const routeAlerts = segments.flatMap((segment: any, segmentIndex: number) => {
        const destination = routePoints.find((point: any) => point.name === segment.destination?.name);
        return (segment.specificRisks || []).map((risk: any, riskIndex: number) => ({
            ...(typeof risk === 'object' && risk ? risk : {}),
            id: `itinerary-risk-${segmentIndex}-${riskIndex}`,
            title: typeof risk === 'string' ? risk : risk?.title || risk?.risk || risk?.description,
            summary: risk?.description || segment.analysis || segment.mitigation,
            type: 'threat',
            lat: destination?.lat,
            lng: destination?.lng,
            location: { name: segment.destination?.name || segment.legName },
            riskLevel: risk?.riskLevel || risk?.level || segment.riskLevel || result.overallRiskLevel,
            riskJustification: risk?.riskJustification || 'Saved itinerary risk level; no separate numerical item estimate was supplied.'
        }));
    });

    return (
        <div className="space-y-8 animate-fade-in pb-12">
            <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white p-8 rounded-2xl shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                     {React.isValidElement(ICONS.map) ? React.cloneElement(ICONS.map as any, { className: 'h-40 w-40' }) : null}
                </div>
                <div className="relative z-10 max-w-2xl">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="bg-white/20 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Risk Analysis Workflow</span>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${result.overallRiskLevel === 'Low' ? 'bg-green-500' : 'bg-red-500'}`}>
                            {result.overallRiskLevel} Risk
                        </span>
                    </div>
                    <h3 className="text-3xl font-black mb-4">{result.itineraryName}</h3>
                    <div className="prose prose-invert prose-sm max-w-none text-blue-100 font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(result.executiveSummary || '') }} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    <Section title="Operational Route Analysis">
                        <div className="w-full h-[500px] rounded-2xl overflow-hidden border border-slate-300 shadow-xl relative group">
                            <UnifiedMap
                                isLoaded={isLoaded}
                                theme="light"
                                initialViewState={{ latitude: mapCenter.lat, longitude: mapCenter.lng, zoom: routePoints.length ? 6 : 2 }}
                                tacticalRoutes={tacticalRoutes}
                                alerts={routeAlerts}
                                sidebarTitle="ITINERARY ROUTE INTELLIGENCE"
                                markers={[...(result.mapMarkers || []), ...routePoints.map((point: any, idx: number) => ({
                                    ...point,
                                    id: `stop-${idx}`,
                                    lat: point.lat,
                                    lng: point.lng,
                                    name: point.name || `Stop ${idx + 1}`,
                                    title: point.name || `Stop ${idx + 1}`,
                                    type: 'RouteStop',
                                    description: 'Itinerary waypoint'
                                }))]}
                            />
                            <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow-md border border-slate-200">
                                 <p className="text-[10px] font-black uppercase text-blue-600 tracking-widest">Active Route Protocol</p>
                                 <p className="text-xs font-bold text-slate-800">{segments.length} Operational Segments</p>
                            </div>
                        </div>
                    </Section>
                </div>

                <div className="space-y-6">
                    <RecommendedSuitesDisplay suites={result.recommendedSuites || []} onNavigate={onNavigate || (() => {})} />
                    
                    <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-lg border border-slate-800">
                         <h4 className="font-black uppercase text-xs mb-4 tracking-widest border-b border-slate-800 pb-2 flex items-center gap-2">
                             {React.isValidElement(ICONS.shield) ? React.cloneElement(ICONS.shield as any, { className: 'h-4 w-4 text-blue-400' }) : null} Security Posture
                         </h4>
                         <div className="space-y-4">
                             <div className="flex justify-between items-center bg-slate-800/50 p-3 rounded-xl">
                                 <span className="text-[10px] font-bold uppercase text-slate-400">Total Distance</span>
                                 <span className="text-sm font-black">Calculated on Map</span>
                             </div>
                             <div className="flex justify-between items-center bg-slate-800/50 p-3 rounded-xl">
                                 <span className="text-[10px] font-bold uppercase text-slate-400">Risk Threshold</span>
                                 <span className={`text-sm font-black ${result.overallRiskLevel === 'Low' ? 'text-green-400' : 'text-red-400'}`}>{result.overallRiskLevel}</span>
                             </div>
                         </div>
                    </div>
                </div>
            </div>

            <Section title="Segmented Risk Timeline">
                {segments.length === 0 && (
                    <div className="p-6 text-center text-slate-500 bg-slate-50 rounded-xl border border-slate-200">
                        <p className="font-semibold">No itinerary segments were generated.</p>
                        <p className="text-sm mt-1">Please re-run the analysis with at least one travel leg defined.</p>
                    </div>
                )}
                <div className="relative border-l-2 border-slate-200 ml-3 space-y-8 py-4">
                    {segments.map((segment: any, i: number) => (
                        <div key={i} className="relative pl-10 group">
                            <div className="absolute left-[-9px] top-1 h-4 w-4 rounded-full bg-blue-600 border-2 border-white shadow-md z-10 group-hover:scale-125 transition-transform"></div>
                            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm group-hover:shadow-lg group-hover:border-blue-300 transition-all duration-300">
                                <div className="flex flex-col md:flex-row justify-between gap-4 mb-4 pb-4 border-b border-slate-100">
                                    <div>
                                        <p className="text-[10px] font-black uppercase text-blue-600 mb-1 tracking-widest">{formatLocalDate(segment.date, segment.date || '')}</p>
                                        <h4 className="text-lg font-black text-slate-800">{segment.legName}</h4>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-right">
                                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Transport Mode</p>
                                            <p className="text-sm font-black text-slate-700">{segment.mode}</p>
                                        </div>
                                        <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-500">
                                             {String(segment.mode || '').toLowerCase().includes('air') ? ICONS.briefcase : ICONS.location}
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mb-4">
                                    <p className="text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Leg Risk Analysis</p>
                                    <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(segment.analysis || segment.riskAnalysis || segment.assessment || 'Detailed leg analysis unavailable.') }} />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Tactical Vulnerabilities</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(segment.specificRisks || segment.risks || []).map((risk: any, idx: number) => (
                                                <span key={idx} className="bg-red-50 text-red-700 border border-red-100 px-3 py-1 rounded-full text-[10px] font-bold">
                                                    {typeof risk === 'string' ? risk : risk?.title || risk?.risk || risk?.description}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Direct Action Guidance</p>
                                        <p className="text-xs text-slate-700 font-semibold leading-relaxed p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                                            {segment.mitigation}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
};

export default ItineraryRiskDisplay;
